import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import api, { API_ENDPOINTS } from '../config/api';
import { stripHtmlTags } from '../utils/htmlUtils';
import { JOB_CATEGORIES, formatCategoryName } from '../constants/categories';
import { 
  Search, 
  Filter, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users,
  Star,
  TrendingUp,
  CheckCircle,
  Calendar,
  Briefcase
} from 'lucide-react';

const JobsPage = () => {
  const { user, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    category: '',
    type: '',
    minBudget: '',
    maxBudget: '',
    search: '',
    page: 1
  });

  // Handle URL parameters for type filtering
  useEffect(() => {
    const typeFromUrl = searchParams.get('type');
    if (typeFromUrl) {
      setFilters(prev => ({ ...prev, type: typeFromUrl }));
    }
  }, [searchParams]);

  // Fetch jobs with current filters
  const { data: jobsData, isLoading, error } = useQuery(
    ['jobs', filters],
    () => api.get(API_ENDPOINTS.JOBS.LIST, { params: filters }),
    {
      select: (data) => data.data,
      keepPreviousData: true,
    }
  );

  const jobs = jobsData?.jobs || [];
  const totalPages = jobsData?.totalPages || 1;
  const currentPage = jobsData?.currentPage || 1;

  const categories = Object.keys(JOB_CATEGORIES);

  const jobTypes = [
    { value: '', label: 'All Types' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'full-time', label: 'Full-time' },
    { value: 'contract', label: 'Contract' }
  ];

  const budgetRanges = [
    { value: '', label: 'Any Budget' },
    { value: '0-500', label: '$0 - $500' },
    { value: '500-1500', label: '$500 - $1,500' },
    { value: '1500-5000', label: '$1,500 - $5,000' },
    { value: '5000+', label: '$5,000+' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const formatBudget = (job) => {
    if (!job.budget) return 'Budget negotiable';
    if (job.budget.fixed) return `$${job.budget.fixed.toFixed(2)}`;
    if (job.budget.min && job.budget.max) {
      return `$${job.budget.min.toFixed(2)} - $${job.budget.max.toFixed(2)}`;
    }
    if (job.budget.min) return `$${job.budget.min.toFixed(2)}+`;
    return 'Budget negotiable';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-heading mb-2">
          Browse Jobs
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Find amazing freelance and full-time opportunities from clients worldwide
        </p>
      </div>

      {/* Search and Filters */}
      <div className="card p-4 sm:p-6 mb-6">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search jobs, skills, companies..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            />
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full input-field"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {JOB_CATEGORIES[cat]?.label || formatCategoryName(cat)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Job Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full input-field"
            >
              {jobTypes.map(jobType => (
                <option key={jobType.value} value={jobType.value}>
                  {jobType.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Budget Range
            </label>
            <select
              value={`${filters.minBudget}-${filters.maxBudget}`}
              onChange={(e) => {
                const [min, max] = e.target.value.split('-');
                handleFilterChange('minBudget', min || '');
                handleFilterChange('maxBudget', max || '');
              }}
              className="w-full input-field"
            >
              {budgetRanges.map(range => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end sm:col-span-2 lg:col-span-1">
            <button className="btn-outline w-full text-sm sm:text-base">
              <Filter className="w-4 h-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Jobs Grid */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {jobs.map((job) => (
          <div key={job._id} className="card p-4 sm:p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
              {/* Job Image - Circular, Small, Left */}
              <div className="flex items-center gap-3 sm:block sm:flex-shrink-0">
                {job.imageUrl ? (
                  <div className="flex-shrink-0">
                    <img 
                      src={job.imageUrl} 
                      alt={job.title}
                      className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                    />
                  </div>
                ) : (
                  <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 dark:text-gray-500" />
                  </div>
                )}
                
                {/* Mobile: Title and Tag next to image */}
                <div className="sm:hidden flex-1 min-w-0">
                  <div className="flex items-start flex-wrap gap-2 mb-2">
                    <Link 
                      to={`/jobs/${job._id}`}
                      className="text-lg font-semibold text-heading hover:text-accent transition-colors duration-200 line-clamp-2"
                    >
                      {job.title}
                    </Link>
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                      job.type === 'full-time' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      job.type === 'part-time' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      job.type === 'freelance' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                      'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                    }`}>
                      {job.type.charAt(0).toUpperCase() + job.type.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Content - Right of Image */}
              <div className="flex-1 min-w-0 w-full sm:w-auto">
                {/* Desktop: Title and Tag */}
                <div className="hidden sm:block mb-3">
                  <div className="flex items-start flex-wrap gap-2 mb-2">
                    <Link 
                      to={`/jobs/${job._id}`}
                      className="text-xl font-semibold text-heading hover:text-accent transition-colors duration-200 line-clamp-1"
                    >
                      {job.title}
                    </Link>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                      job.type === 'full-time' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      job.type === 'part-time' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      job.type === 'freelance' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                      'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                    }`}>
                      {job.type.charAt(0).toUpperCase() + job.type.slice(1)}
                    </span>
                  </div>
                </div>

                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 line-clamp-2">
                  {stripHtmlTags(job.description)}
                </p>

                {/* Job Parameters */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="whitespace-nowrap">{formatBudget(job)}</span>
                  </div>
                  
                  {job.duration && job.duration.value && job.duration.unit && (
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="whitespace-nowrap">{job.duration.value} {job.duration.unit}</span>
                    </div>
                  )}

                  {job.location && (
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="whitespace-nowrap">
                        {job.location.remote && 'Remote'}
                        {job.location.remote && (job.location.city || job.location.country) && ' • '}
                        {(job.location.city || job.location.country) && (
                          <>
                            {job.location.city && job.location.city}
                            {job.location.city && job.location.country && ', '}
                            {job.location.country && job.location.country}
                          </>
                        )}
                        {!job.location.remote && !job.location.city && !job.location.country && 'Location not specified'}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center space-x-1">
                    <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="whitespace-nowrap">{job.applicationCount} applications</span>
                  </div>
                </div>

                {/* Skills */}
                <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
                  {job.skills?.slice(0, 5).map((skill, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-md"
                    >
                      {skill}
                    </span>
                  ))}
                  {job.skills?.length > 5 && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 text-xs rounded-md">
                      +{job.skills.length - 5} more
                    </span>
                  )}
                </div>

                {/* Footer: Client Info and Action */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2 pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-2 w-full sm:w-auto">
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-medium flex-shrink-0">
                      {job.client?.username?.[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1 sm:flex-initial">
                      <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {job.client?.username}
                      </div>
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Star className="w-3 h-3 fill-current text-yellow-400" />
                        <span>{job.client?.stats?.rating?.average?.toFixed(1) || '0'}</span>
                        <span>({job.client?.stats?.rating?.count || 0} reviews)</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-2">
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      Posted {new Date(job.createdAt).toLocaleDateString()}
                    </span>
                    {isAuthenticated && user?.role === 'talent' ? (
                      <Link
                        to={`/jobs/${job._id}`}
                        className="btn-primary text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                      >
                        Apply Now
                      </Link>
                    ) : (
                      <Link
                        to={`/jobs/${job._id}`}
                        className="btn-outline text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                      >
                        View Details
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6 sm:mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Previous
          </button>

          <div className="flex items-center gap-1 sm:gap-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-2.5 sm:px-3 py-2 text-xs sm:text-sm rounded-lg min-w-[2.5rem] sm:min-w-[3rem] ${
                    page === currentPage
                      ? 'bg-accent text-white'
                      : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Next
          </button>
        </div>
      )}

      {/* No Results */}
      {!isLoading && jobs.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No jobs found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search criteria or check back later for new postings.
          </p>
        </div>
      )}
    </div>
  );
};

export default JobsPage;
